/**
 * BOSCO Studio™ Expert Mode — iter 77
 *
 * Elite Individual-only command-line academic OS.
 * Strict command-only input (no NLP). Black terminal with JetBrains Mono.
 * ASCII logo fades on typing (100 → 0 opacity).
 */
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { LogOut, Loader2, Sparkles, X, Paperclip, FileDown, FileText, Languages, GraduationCap, BookOpen, ScrollText, Award } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const TOKEN_KEY = 'sip_token';
const ONBOARDING_KEY = 'bosco_studio_onboarding_dismissed';
const CONTEXT_KEY = 'bosco_studio_context_v1';

// flag → label, icon, placeholder
const CONTEXT_FIELDS = [
  { key: 'lang',    flag: '--lang',    icon: Languages,      label: 'Language' },
  { key: 'board',   flag: '--board',   icon: ScrollText,     label: 'Board' },
  { key: 'klass',   flag: '--class',   icon: GraduationCap,  label: 'Class' },
  { key: 'subject', flag: '--subject', icon: BookOpen,       label: 'Subject', quote: true },
  { key: 'exam',    flag: '--exam',    icon: Award,          label: 'Exam' },
];

const ASCII_LOGO = `
 ██████╗  ██████╗ ███████╗ ██████╗ ██████╗
 ██╔══██╗██╔═══██╗██╔════╝██╔════╝██╔═══██╗
 ██████╔╝██║   ██║███████╗██║     ██║   ██║
 ██╔══██╗██║   ██║╚════██║██║     ██║   ██║
 ██████╔╝╚██████╔╝███████║╚██████╗╚██████╔╝
 ╚═════╝  ╚═════╝ ╚══════╝ ╚═════╝ ╚═════╝
        Behavioral Omnimodal Scholastic
              Cognitive Orchestrator
            ─── Actanon Technologies ───
`;

function api(path, opts = {}) {
  const token = localStorage.getItem(TOKEN_KEY);
  return fetch(`${API_URL}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: token ? `Bearer ${token}` : '',
      ...(opts.headers || {}),
    },
  }).then(async (r) => {
    const data = await r.json().catch(() => ({}));
    if (!r.ok) {
      const err = new Error(data?.detail?.message || data?.detail || r.statusText);
      err.detail = data?.detail;
      err.status = r.status;
      throw err;
    }
    return data;
  });
}

export default function BoscoStudioPage() {
  const navigate = useNavigate();
  const [access, setAccess] = useState(null);
  const [commands, setCommands] = useState({ native: [], custom: [] });
  const [input, setInput] = useState('');
  const [history, setHistory] = useState([]);    // execution history
  const [cmdLog, setCmdLog] = useState([]);      // user's input history for arrow-up nav
  const [logIdx, setLogIdx] = useState(-1);
  const [running, setRunning] = useState(false);
  const [showHints, setShowHints] = useState(false);
  const [hintIndex, setHintIndex] = useState(0);
  const inputRef = useRef(null);
  const bottomRef = useRef(null);

  // Onboarding overlay — one-time per device unless user opts to see it again.
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(true);
  useEffect(() => {
    try {
      if (localStorage.getItem(ONBOARDING_KEY) !== '1') {
        setShowOnboarding(true);
      }
    } catch { /* ignore */ }
  }, []);
  const closeOnboarding = useCallback(() => {
    try {
      if (dontShowAgain) localStorage.setItem(ONBOARDING_KEY, '1');
    } catch { /* ignore */ }
    setShowOnboarding(false);
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [dontShowAgain]);

  // ─── Sticky context (board/class/lang/subject/exam) ────────────
  const [context, setContext] = useState(() => {
    try { return JSON.parse(localStorage.getItem(CONTEXT_KEY) || '{}'); }
    catch { return {}; }
  });
  useEffect(() => {
    try { localStorage.setItem(CONTEXT_KEY, JSON.stringify(context)); } catch { /* ignore */ }
  }, [context]);
  const clearContextField = (key) => setContext((c) => { const n = { ...c }; delete n[key]; return n; });

  // ─── File upload (ingest) ──────────────────────────────────────
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const onUploadClick = () => fileInputRef.current?.click();
  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    setHistory((h) => [...h, { type: 'system', text: `Uploading ${file.name} (${(file.size / 1024).toFixed(1)} KB)…` }]);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = localStorage.getItem(TOKEN_KEY) || '';
      const r = await fetch(`${API_URL}/api/bosco/ingest`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await r.json();
      if (!r.ok) {
        const msg = data?.detail?.message || data?.detail || `Upload failed (${r.status})`;
        throw new Error(msg);
      }
      setHistory((h) => [...h, {
        type: 'system',
        text:
          `✓ Ingested ${data.kind.toUpperCase()} file '${data.filename}' (${data.chars} chars).\n` +
          `Reference id: ${data.ingestId}\n` +
          `Preview: ${(data.preview || '').slice(0, 240)}${(data.preview || '').length > 240 ? '…' : ''}\n` +
          `→ ${data.hint}`,
      }]);
      // Auto-prefill the input so user can pick a command
      setInput(`/extract-questions --from ${data.ingestId} `);
      setTimeout(() => inputRef.current?.focus(), 50);
    } catch (err) {
      setHistory((h) => [...h, { type: 'error', text: `Upload failed: ${err.message}` }]);
    } finally {
      setUploading(false);
    }
  };

  // ─── Export (PDF / DOCX) ───────────────────────────────────────
  const exportOutput = async (execId, command, format) => {
    const token = localStorage.getItem(TOKEN_KEY) || '';
    try {
      const r = await fetch(`${API_URL}/api/bosco/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ execId, format }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e?.detail?.message || `Export failed (${r.status})`);
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const safeCmd = (command || 'bosco').replace(/[^a-zA-Z0-9-]/g, '_').replace(/^_+|_+$/g, '');
      const a = document.createElement('a');
      a.href = url;
      a.download = `bosco_${safeCmd}_${execId.slice(-8)}.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setHistory((h) => [...h, { type: 'error', text: `Export failed: ${err.message}` }]);
    }
  };

  // ─── Pattern Recipe (Phase 4.5) ────────────────────────────────
  // `/generate-from-pattern --from ing_* --variants N [--exam jee-main] [--email teacher@x.com]`
  const runPatternRecipe = async (composed) => {
    const parseFlag = (re) => {
      const m = composed.match(re);
      return m ? (m[1] || m[2] || '').trim() : null;
    };
    const ingestId = parseFlag(/--from\s+(\S+)/i);
    const variants = parseInt(parseFlag(/--variants\s+(\d+)/i) || '3', 10);
    const exam = parseFlag(/--exam\s+([a-zA-Z0-9-]+)/i);
    const email = parseFlag(/--email\s+(\S+)/i);
    const lang = parseFlag(/--lang\s+([a-zA-Z-]+)/i) || context.lang || null;
    const board = parseFlag(/--board\s+([a-zA-Z-]+)/i) || context.board || null;
    const klass = parseFlag(/--(?:class|grade)\s+(\S+)/i) || context.klass || null;
    const subject = parseFlag(/--subject\s+"([^"]+)"|--subject\s+(\S+)/i) || context.subject || null;

    if (!ingestId) {
      setHistory((h) => [...h, {
        type: 'error',
        text:
          'Pattern Recipe requires --from <ingestId>. Upload a previous-year paper first (📎),\n' +
          'then run: /generate-from-pattern --from ing_xxxx --variants 3 [--exam jee-main] [--email teacher@x.com]',
      }]);
      return;
    }
    const token = localStorage.getItem(TOKEN_KEY) || '';
    setHistory((h) => [...h, {
      type: 'system',
      text:
        `Starting Pattern Recipe job — ${variants} ORIGINAL variant${variants !== 1 ? 's' : ''} from ${ingestId}…\n` +
        `Running ${variants} parallel LLM calls; this typically takes 40–120 seconds.`,
    }]);
    try {
      const r = await fetch(`${API_URL}/api/bosco/generate-from-pattern`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ingestId, variants, exam, email, lang, board, klass, subject }),
      });
      const startData = await r.json();
      if (!r.ok) {
        const msg = startData?.detail?.message || startData?.detail || `Pattern Recipe failed (${r.status})`;
        throw new Error(msg);
      }
      const jobId = startData.jobId;
      setHistory((h) => [...h, {
        type: 'system',
        text: `Job started: ${jobId} (exam: ${startData.examLabel}). Polling every 5s…`,
      }]);
      // Poll for completion (max 5 min)
      const deadline = Date.now() + 5 * 60 * 1000;
      let last = null;
      while (Date.now() < deadline) {
        await new Promise((res) => setTimeout(res, 5000));
        const pr = await fetch(`${API_URL}/api/bosco/generate-from-pattern/${jobId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!pr.ok) {
          throw new Error(`Polling failed (${pr.status})`);
        }
        last = await pr.json();
        if (last.status === 'done' || last.status === 'failed') break;
      }
      if (!last || (last.status !== 'done' && last.status !== 'failed')) {
        throw new Error('Pattern Recipe timed out after 5 minutes.');
      }
      if (last.status === 'failed') {
        throw new Error(last.error || 'Pattern Recipe failed.');
      }
      const succ = (last.results || []).filter((x) => x.ok);
      const summary =
        `✓ Generated ${succ.length} original variants in ${last.examLabel}.\n` +
        succ.map((v) => `  • Variant ${v.variant} — execId ${v.execId.slice(-8)}`).join('\n') +
        (last.email?.requested
          ? `\n📧 Email: ${last.email.sent ? `sent to ${last.email.to} ✓` : `failed (${last.email.error || 'unknown'})`}`
          : '');
      setHistory((h) => [...h, { type: 'system', text: summary }]);
      succ.forEach((v) => {
        setHistory((h) => [...h, {
          type: 'output',
          text: `**${v.title}** — original variant generated. Use the buttons → to download as PDF/DOCX.`,
          command: '/generate-from-pattern',
          spec: { category: 'exam' },
          execId: v.execId,
          meta: { exam: last.exam, ingestId },
        }]);
      });
    } catch (err) {
      setHistory((h) => [...h, { type: 'error', text: `Pattern Recipe failed: ${err.message}` }]);
    }
  };

  // Opacity ladder per spec: 100 → 80 → 50 → 20 → 0
  const logoOpacity = useMemo(() => {
    const len = input.length;
    if (len === 0) return 1.0;
    if (len <= 3) return 0.8;
    if (len <= 8) return 0.5;
    if (len <= 15) return 0.2;
    return 0;
  }, [input.length]);

  // Boot
  useEffect(() => {
    (async () => {
      try {
        const a = await api('/api/bosco/access');
        setAccess(a);
        if (a.studioAvailable) {
          const c = await api('/api/bosco/commands');
          setCommands(c);
          setHistory((h) => [
            ...h,
            {
              type: 'system',
              text:
                `BOSCO Studio™ v1.0 — Expert Mode\n` +
                `${c.totalNative} native commands loaded. ${c.custom.length} custom.\n` +
                `Type / to browse. TAB to autocomplete. ↑/↓ for history. /help for catalog.`,
            },
          ]);
        }
      } catch (e) {
        setAccess({ elite: false, studioAvailable: false, error: e.message });
      }
    })();
  }, []);

  // Auto-scroll
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [history]);

  // Build flat command list for autocomplete
  const allCommands = useMemo(() => {
    const arr = [
      ...(commands.native || []).map((c) => ({ ...c, source: 'native' })),
      ...(commands.custom || []).map((c) => ({ ...c, source: 'custom' })),
    ];
    return arr;
  }, [commands]);

  // Hints filtering
  const hints = useMemo(() => {
    if (!input.startsWith('/')) return [];
    const q = input.split(/\s+/)[0].toLowerCase();
    if (q === '/') return allCommands.slice(0, 12);
    return allCommands
      .filter((c) => c.name.startsWith(q) || (c.alias && c.alias.startsWith(q)))
      .slice(0, 12);
  }, [input, allCommands]);

  const acceptHint = (cmd) => {
    setInput(cmd.name + ' ');
    setShowHints(false);
    inputRef.current?.focus();
  };

  const onKeyDown = (e) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      if (hints.length > 0) acceptHint(hints[hintIndex] || hints[0]);
      return;
    }
    if (showHints && hints.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setHintIndex((i) => Math.min(hints.length - 1, i + 1)); return; }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setHintIndex((i) => Math.max(0, i - 1)); return; }
    } else {
      if (e.key === 'ArrowUp' && cmdLog.length > 0) {
        e.preventDefault();
        const next = Math.min(cmdLog.length - 1, logIdx + 1);
        setLogIdx(next);
        setInput(cmdLog[cmdLog.length - 1 - next] || '');
        return;
      }
      if (e.key === 'ArrowDown' && logIdx > 0) {
        e.preventDefault();
        const next = logIdx - 1;
        setLogIdx(next);
        setInput(cmdLog[cmdLog.length - 1 - next] || '');
        return;
      }
    }
    if (e.key === 'Escape') {
      setShowHints(false);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      onSubmit();
    }
  };

  // Compose final command: append sticky context flags that user has not already supplied,
  // and learn new context from explicit flags so future commands inherit them.
  const composeWithContext = useCallback((raw) => {
    let out = raw;
    const lower = ` ${raw} `.toLowerCase();
    CONTEXT_FIELDS.forEach(({ key, flag, quote }) => {
      const hasFlag = lower.includes(` ${flag} `);
      if (!hasFlag && context[key]) {
        const val = quote ? `"${context[key]}"` : context[key];
        out += ` ${flag} ${val}`;
      }
    });
    return out;
  }, [context]);

  const learnContextFromRaw = useCallback((raw) => {
    const re = {
      lang: /--lang\s+([a-zA-Z-]+)/i,
      board: /--board\s+([a-zA-Z-]+)/i,
      klass: /--(?:class|grade)\s+(\S+)/i,
      subject: /--subject\s+"([^"]+)"|--subject\s+(\S+)/i,
      exam: /--exam\s+([a-zA-Z0-9-]+)/i,
    };
    const learned = {};
    Object.entries(re).forEach(([k, r]) => {
      const m = raw.match(r);
      if (m) learned[k] = (m[1] || m[2] || '').trim();
    });
    if (Object.keys(learned).length) setContext((c) => ({ ...c, ...learned }));
  }, []);

  const onSubmit = useCallback(async () => {
    const raw = input.trim();
    if (!raw) return;
    if (!raw.startsWith('/')) {
      const closest = allCommands.find((c) => c.name.includes(raw.slice(0, 4)));
      setHistory((h) => [
        ...h,
        { type: 'input', text: raw },
        {
          type: 'error',
          text: `Command not recognized. BOSCO Studio is command-only.${closest ? `\nDid you mean: ${closest.name} — press TAB to accept.` : ''}\nNatural language is intentionally disabled.`,
        },
      ]);
      setInput('');
      return;
    }
    if (raw === '/clear' || raw === '/cls') {
      setHistory([]);
      setInput('');
      return;
    }
    if (raw === '/help' || raw === '/?') {
      setHistory((h) => [
        ...h,
        { type: 'input', text: raw },
        { type: 'system', text: _buildHelpText(allCommands) },
      ]);
      setInput('');
      return;
    }
    if (raw === '/upload' || raw === '/u') {
      setHistory((h) => [
        ...h,
        { type: 'input', text: raw },
        { type: 'system', text: 'Opening file picker… (paperclip icon also works). Supported: PDF, DOCX, TXT, MD, PNG, JPG, WEBP.' },
      ]);
      setInput('');
      onUploadClick();
      return;
    }
    learnContextFromRaw(raw);
    const composed = composeWithContext(raw);
    setCmdLog((l) => [...l, raw]);
    setLogIdx(-1);
    setHistory((h) => [...h, { type: 'input', text: composed === raw ? raw : `${raw}    [+ context: ${composed.slice(raw.length).trim()}]` }]);
    setInput('');
    setRunning(true);
    try {
      // Special-case: /generate-from-pattern uses a dedicated endpoint
      if (/^\/(generate-from-pattern|gfp)\b/.test(composed)) {
        await runPatternRecipe(composed);
        setRunning(false);
        return;
      }
      const res = await api('/api/bosco/execute', {
        method: 'POST',
        body: JSON.stringify({ command: composed }),
      });
      setHistory((h) => [...h, {
        type: 'output',
        text: res.result,
        command: res.command,
        spec: res.spec,
        execId: res.execId,
        meta: { lang: res.lang, board: res.board, class: res.class, subject: res.subject, exam: res.exam, ingestId: res.ingestId },
      }]);
    } catch (e) {
      let msg = e.message;
      if (e.detail && typeof e.detail === 'object' && e.detail.suggestions) {
        msg += `\nDid you mean: ${e.detail.suggestions.join(', ')}`;
      }
      setHistory((h) => [...h, { type: 'error', text: msg }]);
    } finally {
      setRunning(false);
    }
  }, [input, allCommands, composeWithContext, learnContextFromRaw]);

  // ─── Access gates ─────────────────────────────────────────────
  if (access === null) {
    return (
      <div style={{ background: '#000', color: '#e5e7eb', minHeight: '100vh' }} className="flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }
  if (!access.studioAvailable) {
    return (
      <div style={{ background: '#000', color: '#e5e7eb' }} className="min-h-screen flex items-center justify-center p-6 font-mono">
        <div className="max-w-md border border-amber-500/40 rounded-lg p-6 bg-amber-500/5">
          <div className="text-amber-400 text-xs uppercase tracking-wider mb-2">Access Denied</div>
          <h2 className="text-xl text-amber-100 font-bold">BOSCO Studio™ — Elite Only</h2>
          <p className="text-sm text-slate-300 mt-3 leading-relaxed">
            BOSCO Studio™ Expert Mode is exclusively available to <strong>Elite Individual</strong> subscribers.
            Your current plan is <code className="text-amber-300">{(access.tier || 'free').toUpperCase()}</code>.
          </p>
          <div className="mt-5 flex gap-2">
            <button
              onClick={() => navigate('/pricing')}
              className="px-4 py-2 rounded bg-amber-500 text-black font-semibold text-sm hover:bg-amber-400"
              data-testid="bosco-upgrade-cta"
            >
              Upgrade to Elite
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 rounded border border-slate-700 text-slate-300 text-sm hover:bg-slate-900"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Studio render ────────────────────────────────────────────
  return (
    <div
      data-clarity-mask="true"
      data-testid="bosco-studio"
      style={{
        background: '#000',
        color: '#e5e7eb',
        minHeight: '100vh',
        fontFamily: '"JetBrains Mono", "Cascadia Code", "Fira Code", Menlo, monospace',
      }}
      className="flex flex-col"
      onClick={() => { if (!showOnboarding) inputRef.current?.focus(); }}
    >
      {showOnboarding && (
        <BoscoOnboarding
          dontShowAgain={dontShowAgain}
          setDontShowAgain={setDontShowAgain}
          onClose={closeOnboarding}
        />
      )}
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-slate-800 text-xs">
        <div className="flex items-center gap-3">
          <span className="text-emerald-400">●</span>
          <span className="text-slate-400">BOSCO Studio™ Expert Mode</span>
          <span className="text-slate-600">·</span>
          <span className="text-slate-500 hidden sm:inline">Behavioral Omnimodal Scholastic Cognitive Orchestrator</span>
          <span className="text-slate-600 hidden lg:inline">·</span>
          <span className="text-slate-500 hidden lg:inline">Actanon Technologies</span>
        </div>
        <button
          onClick={() => navigate('/dashboard')}
          className="text-slate-400 hover:text-amber-300 inline-flex items-center gap-1.5"
          data-testid="bosco-switch-to-button-mode"
          title="Switch back to button mode"
        >
          <LogOut className="h-3.5 w-3.5" />
          Switch to button mode
        </button>
      </div>

      {/* ASCII logo + history scrollback */}
      <div className="flex-1 overflow-y-auto px-4 py-6">
        <pre
          className="text-center text-emerald-400 text-[10px] sm:text-xs leading-tight transition-opacity duration-300 mb-6 select-none"
          style={{ opacity: logoOpacity }}
        >
          {ASCII_LOGO}
        </pre>

        {history.map((h, i) => (
          <LogLine key={i} entry={h} onExport={exportOutput} />
        ))}
        {running && (
          <div className="text-slate-500 text-sm flex items-center gap-2 my-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin text-emerald-500" />
            Executing…
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input bar */}
      <div className="border-t border-slate-800 px-4 py-3 sticky bottom-0 bg-black">
        {/* Sticky context chips (Phase 4) */}
        {Object.values(context).some(Boolean) && (
          <div className="mb-2 flex items-center flex-wrap gap-1.5" data-testid="bosco-context-chips">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 mr-1">Context →</span>
            {CONTEXT_FIELDS.map(({ key, flag, icon: Icon, label }) =>
              context[key] ? (
                <span
                  key={key}
                  data-testid={`bosco-chip-${key}`}
                  className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-emerald-500/30 bg-emerald-500/5 text-emerald-200 text-[11px]"
                  title={`${label}: ${context[key]} (auto-applied unless overridden)`}
                >
                  <Icon className="h-3 w-3 opacity-70" />
                  <span className="font-mono">{flag}</span>
                  <span className="text-emerald-300">{context[key]}</span>
                  <button
                    type="button"
                    onClick={() => clearContextField(key)}
                    className="ml-0.5 text-emerald-300/60 hover:text-rose-300"
                    aria-label={`Clear ${label}`}
                    data-testid={`bosco-chip-clear-${key}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ) : null
            )}
          </div>
        )}
        {showHints && hints.length > 0 && (
          <div className="mb-2 border border-slate-800 bg-slate-950 rounded text-xs max-h-56 overflow-y-auto" data-testid="bosco-hints">
            {hints.map((h, i) => (
              <div
                key={h.name + i}
                onClick={() => acceptHint(h)}
                className={`px-3 py-1.5 cursor-pointer flex items-center justify-between ${
                  i === hintIndex ? 'bg-emerald-900/30 text-emerald-200' : 'text-slate-300 hover:bg-slate-900'
                }`}
              >
                <span><span className="text-emerald-400">{h.name}</span> <span className="text-slate-500 ml-2">{h.description}</span></span>
                <span className="text-slate-600">{h.category}{h.source === 'custom' ? ' · custom' : ''}</span>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2 text-sm">
          <span className="text-emerald-500 font-bold">bosco ❯</span>
          <button
            type="button"
            onClick={onUploadClick}
            disabled={uploading || running}
            data-testid="bosco-upload-btn"
            title="Upload reference file (PDF, DOCX, image)"
            className="text-slate-500 hover:text-emerald-400 disabled:opacity-40 transition-colors"
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            data-testid="bosco-file-input"
            accept=".pdf,.docx,.txt,.md,.png,.jpg,.jpeg,.webp,.heic,.heif,image/*"
            onChange={onFileChosen}
          />
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => { setInput(e.target.value); setShowHints(e.target.value.startsWith('/')); setHintIndex(0); }}
            onKeyDown={onKeyDown}
            onFocus={() => setShowHints(input.startsWith('/'))}
            spellCheck={false}
            autoCorrect="off"
            autoCapitalize="off"
            placeholder="/explain photosynthesis --lang hindi   ·   /mock-test --exam jee-main --subject Physics   ·   /help"
            disabled={running}
            className="flex-1 bg-transparent outline-none caret-emerald-400 text-slate-100 placeholder:text-slate-600"
            data-testid="bosco-input"
          />
        </div>
        <div className="text-[10px] text-slate-600 mt-1">
          TAB autocomplete · ↑/↓ history · 📎 attach file · context chips auto-applied · Strict command-only mode (NLP disabled)
        </div>
      </div>
    </div>
  );
}


function LogLine({ entry, onExport }) {
  if (entry.type === 'input') {
    return (
      <div className="my-1 text-slate-100 text-sm">
        <span className="text-emerald-500">bosco ❯ </span>{entry.text}
      </div>
    );
  }
  if (entry.type === 'system') {
    return <pre className="my-2 text-slate-400 text-xs whitespace-pre-wrap">{entry.text}</pre>;
  }
  if (entry.type === 'error') {
    return (
      <pre className="my-2 text-rose-400 text-sm whitespace-pre-wrap border-l-2 border-rose-500/50 pl-3">
        {entry.text}
      </pre>
    );
  }
  // output
  const meta = entry.meta || {};
  const metaBits = [
    meta.exam && `exam=${meta.exam}`,
    meta.board && `board=${meta.board}`,
    meta.class && `class=${meta.class}`,
    meta.lang && meta.lang !== 'english' && `lang=${meta.lang}`,
    meta.subject && `subject=${meta.subject}`,
    meta.ingestId && `from=${meta.ingestId}`,
  ].filter(Boolean);
  return (
    <div className="my-3 border-l-2 border-emerald-500/40 pl-3" data-testid="bosco-output">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
        <div className="text-[10px] text-emerald-400 uppercase tracking-wider">
          {entry.command} · {entry.spec?.category}
          {metaBits.length > 0 && <span className="ml-2 text-slate-500 normal-case tracking-normal">[{metaBits.join(' · ')}]</span>}
        </div>
        {entry.execId && onExport && (
          <div className="flex items-center gap-1.5" data-testid="bosco-export-actions">
            <button
              type="button"
              onClick={() => onExport(entry.execId, entry.command, 'pdf')}
              data-testid="bosco-export-pdf"
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-emerald-500/30 bg-emerald-500/5 text-[10px] text-emerald-300 hover:bg-emerald-500/15 transition-colors"
              title="Download as PDF"
            >
              <FileDown className="h-3 w-3" /> PDF
            </button>
            <button
              type="button"
              onClick={() => onExport(entry.execId, entry.command, 'docx')}
              data-testid="bosco-export-docx"
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-sky-500/30 bg-sky-500/5 text-[10px] text-sky-300 hover:bg-sky-500/15 transition-colors"
              title="Download as DOCX"
            >
              <FileText className="h-3 w-3" /> DOCX
            </button>
          </div>
        )}
      </div>
      <div className="prose prose-sm prose-invert max-w-none text-slate-200 leading-relaxed">
        <ReactMarkdown>{entry.text || ''}</ReactMarkdown>
      </div>
    </div>
  );
}

function _buildHelpText(all) {
  const byCat = {};
  all.forEach((c) => {
    byCat[c.category] = byCat[c.category] || [];
    byCat[c.category].push(c);
  });
  let s = `BOSCO Language™ v1.0 — ${all.length} commands available.\n\n`;
  for (const cat of Object.keys(byCat).sort()) {
    s += `─── ${cat.toUpperCase()} (${byCat[cat].length}) ───\n`;
    byCat[cat].slice(0, 12).forEach((c) => {
      s += `  ${c.name.padEnd(24)}${c.description}\n`;
    });
    if (byCat[cat].length > 12) s += `  …and ${byCat[cat].length - 12} more\n`;
    s += `\n`;
  }
  s += `\nUsage: /<command> <arguments> [--lang <language>] [--board <board>] [--class <N>] [--subject "<name>"]\n`;
  s += `Examples:\n`;
  s += `  /explain photosynthesis --lang hindi\n`;
  s += `  /lesson-plan quadratic equations --board cbse --class 10\n`;
  s += `  /question-paper --board icse --class 12 --subject "Physics"\n`;
  s += `  /worksheet newton laws --board cambridge --class 9 --lang english\n`;
  s += `\nLanguages: english, hindi, bengali, marathi, tamil, telugu, gujarati, kannada,\n`;
  s += `           malayalam, punjabi, odia, assamese, urdu, sanskrit\n`;
  s += `Boards:    cbse, icse, isc, ib, cambridge, igcse, nios + 18 Indian state boards\n`;
  return s;
}


// ─────────────────────────────────────────────────────────────
// One-time onboarding overlay
// ─────────────────────────────────────────────────────────────
function BoscoOnboarding({ dontShowAgain, setDontShowAgain, onClose }) {
  // Esc to dismiss
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const tips = [
    { kbd: '/help', text: 'List the full command catalog (127 native + your custom commands).' },
    { kbd: 'Tab',   text: 'Autocomplete the highlighted command suggestion as you type.' },
    { kbd: '↑ / ↓', text: 'Cycle through hints OR replay previous commands when no hints are open.' },
    { kbd: 'Esc',   text: 'Close the autocomplete dropdown.' },
    { kbd: '--lang hindi', text: 'Append a language flag to generate output in any major Indian language.' },
  ];

  return (
    <div
      data-testid="bosco-onboarding"
      onClick={(e) => e.stopPropagation()}
      style={{ fontFamily: '"JetBrains Mono", "Cascadia Code", monospace' }}
      className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6 backdrop-blur-md bg-black/85"
    >
      <div className="relative w-full max-w-xl border border-emerald-500/40 bg-gradient-to-br from-emerald-950/60 to-black rounded-lg shadow-[0_0_60px_-15px_rgba(16,185,129,0.5)]">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-slate-500 hover:text-slate-200 transition-colors"
          data-testid="bosco-onboarding-close"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="px-6 pt-6 pb-2">
          <div className="flex items-center gap-2 text-emerald-400 text-[10px] uppercase tracking-[0.25em] mb-2">
            <Sparkles className="h-3.5 w-3.5" />
            Welcome aboard
          </div>
          <h2 className="text-emerald-100 text-xl font-bold leading-snug">
            BOSCO Studio<sup className="text-xs">™</sup> — Expert Mode
          </h2>
          <p className="text-slate-400 text-xs mt-2 leading-relaxed">
            You&apos;re now in command-line academic OS mode. BOSCO is <span className="text-emerald-300">command-only</span> — every input must start with <code className="text-emerald-300">/</code>. Here are a few shortcuts to get you flying:
          </p>
        </div>

        <div className="px-6 py-4 space-y-2.5 border-t border-emerald-500/10">
          {tips.map((t) => (
            <div key={t.kbd} className="flex items-start gap-3 text-xs">
              <span className="inline-block min-w-[110px] text-center px-2 py-1 rounded border border-emerald-500/30 bg-emerald-500/5 text-emerald-300 font-mono whitespace-nowrap">
                {t.kbd}
              </span>
              <span className="text-slate-300 leading-relaxed pt-0.5">{t.text}</span>
            </div>
          ))}
        </div>

        <div className="px-6 py-4 border-t border-emerald-500/10 flex items-center justify-between flex-wrap gap-3">
          <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer select-none" data-testid="bosco-onboarding-dontshow-label">
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              className="accent-emerald-500 h-3.5 w-3.5"
              data-testid="bosco-onboarding-dontshow"
            />
            Do not show again on this device
          </label>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded border border-emerald-500/60 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20 text-xs font-semibold tracking-wide transition-colors"
            data-testid="bosco-onboarding-start"
          >
            Start typing →
          </button>
        </div>
      </div>
    </div>
  );
}
